
/**
 * Zero-knowledge encryption service.
 * All data is encrypted with the user's PIN before being stored locally.
 * In a real-world scenario, we'd use PBKDF2 to derive a key from the PIN.
 */

async function deriveKey(pin: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const rawKey = enc.encode(pin.padEnd(32, '0')); // Simple padding for demo
  return crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptData(data: any, pin: string): Promise<string> {
  const key = await deriveKey(pin);
  const enc = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(JSON.stringify(data))
  );
  
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return btoa(String.fromCharCode(...combined));
}

export async function decryptData(encryptedBase64: string, pin: string): Promise<any> {
  try {
    const key = await deriveKey(pin);
    const combined = new Uint8Array(
      atob(encryptedBase64).split('').map(c => c.charCodeAt(0))
    );
    const iv = combined.slice(0, 12);
    const data = combined.slice(12);
    
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      data
    );
    
    const dec = new TextDecoder();
    return JSON.parse(dec.decode(decrypted));
  } catch (e) {
    throw new Error('Decryption failed. Invalid PIN.');
  }
}
